// supervisor-release-deploy-verify.ts
//
// P7 §8/§14: second leg of the release flow — G3 approve() -> DEPLOYING (re-check
// immediately before first side effect, spec §8) -> VERIFYING -> DONE. Abort after
// this leg started (merge/push already happened, supervisor-release-flow.ts) routes
// to rollback-coordinator.ts's STOPPING chain. This module never sleeps (§7.2).
import { randomUUID } from "node:crypto";
import {
  type DeployOperationInput,
  recheckG3ImmediatelyBeforeFirstSideEffect,
  runDeployOperationWithIntentReceipt,
} from "./deployment-adapter.js";
import { approveGate } from "./gate-registry.js";
import type { MissionStore } from "./mission-store.js";
import {
  type RolloutObservation,
  runVerifyingChecks,
  type VerifyingInput,
} from "./release-verification.js";

export {
  type AbortAfterMergeInput,
  type AbortAfterMergeResult,
  handleAbortAfterMerge,
} from "./supervisor-abort-after-merge.js";

export interface ApproveG3Input {
  controlRoot: string;
  requestId: string;
  actorId: string;
  boundActorId: string;
}

/** `approve g3 <request-id>` directive lands here (called by the directive processor). */
export async function approveG3(input: ApproveG3Input) {
  return approveGate(input.controlRoot, {
    gate: "g3",
    requestId: input.requestId,
    actorId: input.actorId,
    boundActorId: input.boundActorId,
  });
}

export interface RunDeployAndVerifyInput {
  store: MissionStore;
  controlRoot: string;
  g3RequestId: string;
  candidateSha: string;
  releaseManifestHash: string;
  target: string;
  approvedTarget: string;
  deploy: Omit<DeployOperationInput, "store">;
  verify: VerifyingInput;
}

export type RunDeployAndVerifyResult =
  | { status: "done" }
  | { status: "g3_recheck_failed"; reason: string }
  | { status: "deploy_failed"; reason: string }
  | { status: "rollout_mismatch"; reason: string }
  | { status: "smoke_failed"; reason: string };

/**
 * DEPLOYING: runs the deploy recipe with an intent receipt (test 42). No G3
 * recheck here — callers do that once, immediately before their FIRST side
 * effect (runDeployAndVerify below; the resume leg, which may skip straight
 * to smoke when the deploy already succeeded — B2-2). Luke 2026-09-30:
 * `binding` names the release in the intent; a recipe stopped mid-run
 * (timeout, signal) is `deploy_outcome_unknown`, never retried; one that never
 * started (spawn error) is a plain failure.
 */
export async function runReleaseDeployStep(input: {
  store: MissionStore;
  deploy: Omit<DeployOperationInput, "store">;
  binding?: { candidateSha: string; releaseDeployHash: string };
}): Promise<
  { status: "deployed" } | { status: "deploy_failed" | "deploy_outcome_unknown"; reason: string }
> {
  const deployResult = await runDeployOperationWithIntentReceipt({
    ...input.deploy,
    store: input.store,
    intentBinding: input.binding && {
      candidate_sha: input.binding.candidateSha,
      release_deploy_hash: input.binding.releaseDeployHash,
    },
  });
  if (deployResult.status === "timed_out" || deployResult.status === "killed") {
    const why =
      deployResult.status === "timed_out" ? `quá ${input.deploy.timeoutMs} ms` : "bị signal giết";
    const reason = `deploy (operation ${deployResult.operationId}) dừng giữa chừng (${why}) — không biết đã áp dụng tới đâu`;
    return { status: "deploy_outcome_unknown", reason };
  }
  if (deployResult.status === "not_started") {
    const sig = deployResult.interrupted;
    const reason =
      sig !== undefined
        ? `Nhận ${sig} ngay trước deploy — chưa deploy. Chạy lại \`avatar autopilot run\` để tiếp.`
        : `Deploy recipe không chạy được (${deployResult.reason}) — chưa có gì được deploy. Kiểm \`argv[0]\` có trên PATH (và \`PATH\` nằm trong \`env_allowlist\`), \`cwd\` tồn tại (tương đối = worktree mission), rồi \`run\` lại.`;
    return { status: "deploy_failed", reason };
  }
  if (deployResult.status === "failed") {
    return {
      status: "deploy_failed",
      reason: `Deploy recipe thất bại (exitCode ${deployResult.result.exitCode}).`,
    };
  }
  return { status: "deployed" };
}

/**
 * VERIFYING. P19d: journaled the same way the deploy is — intent BEFORE the
 * checks run, so a crash between the two is visible as an attempt with no
 * outcome, not as an absence. H14: the intent records whether rollout was
 * actually observed; v1 has no probe, so production says `not_observed`.
 */
export async function runReleaseSmokeStep(input: {
  store: MissionStore;
  candidateSha: string;
  verify: VerifyingInput;
}): Promise<
  | { status: "verified"; rollout: RolloutObservation }
  | { status: "rollout_mismatch" | "smoke_failed"; reason: string }
> {
  const verifyOperationId = randomUUID();
  await input.store.commit([
    {
      event_id: `${verifyOperationId}-intent`,
      operation_id: verifyOperationId,
      delta: {
        kind: "operation_intent",
        operation_id: verifyOperationId,
        operation_kind: "verify",
        params: {
          expected_candidate_sha: input.candidateSha,
          smoke_recipe: input.verify.smokeRecipe.recipe_id,
          rollout: input.verify.artifactRollout === undefined ? "not_observed" : "checked",
        },
      },
    },
  ]);

  const verify = await runVerifyingChecks(input.verify);
  await input.store.commit([
    {
      event_id: `${verifyOperationId}-result`,
      operation_id: verifyOperationId,
      delta: {
        kind: "operation_result",
        operation_id: verifyOperationId,
        outcome: verify.status === "verified" ? "succeeded" : "failed",
      },
    },
  ]);

  if (verify.status === "verified") return { status: "verified", rollout: verify.rollout };
  return { status: verify.status, reason: verify.reason };
}

/**
 * Review L7: NO production caller since H14 — the resume leg composes the two
 * steps above WITH per-step receipts; this one-shot writes none (wiring it
 * into `run` re-opens Bug O). Kept for the P7 tests pinning test 40/41 (G3
 * recheck right before the deploy) and 45 (business smoke required).
 */
export async function runDeployAndVerify(
  input: RunDeployAndVerifyInput,
): Promise<RunDeployAndVerifyResult> {
  const recheck = await recheckG3ImmediatelyBeforeFirstSideEffect({
    controlRoot: input.controlRoot,
    requestId: input.g3RequestId,
    currentCandidateSha: input.candidateSha,
    currentReleaseManifestHash: input.releaseManifestHash,
    currentTarget: input.target,
    approvedTarget: input.approvedTarget,
  });
  if (!recheck.ok) {
    return { status: "g3_recheck_failed", reason: recheck.reason };
  }

  const deployed = await runReleaseDeployStep({ store: input.store, deploy: input.deploy });
  // Test-only composition: an unknown outcome is reported as a failure here.
  if (deployed.status !== "deployed") return { status: "deploy_failed", reason: deployed.reason };

  const verified = await runReleaseSmokeStep({
    store: input.store,
    candidateSha: input.candidateSha,
    verify: input.verify,
  });
  if (verified.status !== "verified") return verified;
  return { status: "done" };
}

/** Spec §7.2: 60s normal, 15s while WAITING_GATE/WAITING_HUMAN — read from config, never hardcoded. */
export function pollIntervalSecForPhase(
  isWaiting: boolean,
  normalSec: number,
  gateSec: number,
): number {
  return isWaiting ? gateSec : normalSec;
}
